import { useSyncExternalStore } from "react";

// Guide progress (checklist ticks, "Mark this guide as done") is kept in this
// browser only, like the preview. localStorage can throw (private windows,
// blocked site data), so every read and write is wrapped and falls back to an
// in-memory copy for the current visit.
const memory = new Map<string, string>();
const listeners = new Set<() => void>();

const readValue = (key: string): string | null => {
  try {
    return window.localStorage.getItem(key) ?? memory.get(key) ?? null;
  } catch {
    return memory.get(key) ?? null;
  }
};

export const writeStoredValue = (key: string, value: string) => {
  memory.set(key, value);
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Storage blocked: the in-memory copy still updates this visit.
  }
  listeners.forEach((listener) => listener());
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  window.addEventListener("storage", listener);
  return () => {
    listeners.delete(listener);
    window.removeEventListener("storage", listener);
  };
};

const readServerValue = () => null;

// One stored string (null until set, and always null on the server).
export function useStoredValue(key: string) {
  return useSyncExternalStore(
    subscribe,
    () => readValue(key),
    readServerValue,
  );
}

const separator = "\u0000";

// Several stored strings at once. The snapshot is one joined string so it
// stays referentially stable between renders.
export function useStoredValues(keys: string[]) {
  const joined = useSyncExternalStore(
    subscribe,
    () => keys.map((key) => readValue(key) ?? "").join(separator),
    () => keys.map(() => "").join(separator),
  );
  return joined.split(separator);
}

// Guide "done" flags, shared by the guide pages and the /guides hub.
export const guideDoneKey = (progressId: string) => `fydo.done.${progressId}`;
